import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const SLIDES = [
  {
    id: 1,
    category: "Art That Hangs With Grace",
    title: "Hoop Embroidery",
    description: "Handcrafted hoop pieces designed with intricate threadwork and stunning visual depth.",
    image: "images/carousel-1.webp",
    themeColor: "#D1B284",
  },
  {
    id: 2,
    category: "Memories Stitched Forever",
    title: "Photo Embroidery",
    description: "Turn your cherished photos into timeless embroidered art with fine detailing.",
    image: "images/carousel-24.webp",
    themeColor: "#8D7B68",
  },
  {
    id: 3,
    category: "Soft. Stylish. Handcrafted.",
    title: "Crochets",
    description: "Unique crochet creations made with warmth, personality, and beautiful textures.",
    image: "images/carousel-3.webp",
    themeColor: "#A4B494",
  },
  {
    id: 4,
    category: "Wearable Thread Elegance",
    title: "Shirt Embroidery",
    description: "Premium shirt embroidery with modern patterns and traditional artistry.",
    image: "images/carousel-4.webp",
    themeColor: "#b49494ff",
  },
];

const AUTOPLAY_DURATION = 5000;

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState("next");
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef(null);
  const touchStart = useRef(0);
  const touchEnd = useRef(0);

  const navigate = useNavigate();

  const handleDiscover = (title) => {
    switch (title) {
      case "Hoop Embroidery":
        navigate("/hoop-embroidery");
        break;
      case "Photo Embroidery":
        navigate("/photo-embroidery");
        break;
      case "Crochets":
        navigate("/accessories");
        break;
      case "Shirt Embroidery":
        navigate("/collection");
        break;
      default:
        navigate("/collection");
    }
  };

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const nextSlide = useCallback(() => {
    if (animating) return;
    setAnimating(true);
    setDirection("next");
    setCurrent((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
  }, [animating]);

  const prevSlide = useCallback(() => {
    if (animating) return;
    setAnimating(true);
    setDirection("prev");
    setCurrent((prev) => (prev === 0 ? SLIDES.length - 1 : prev - 1));
  }, [animating]);

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(nextSlide, AUTOPLAY_DURATION);
    const animTimer = setTimeout(() => setAnimating(false), 1000);
    return () => {
      resetTimeout();
      clearTimeout(animTimer);
    };
  }, [current, nextSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  const handleTouchStart = (e) => { touchStart.current = e.targetTouches[0].clientX; };
  const handleTouchMove = (e) => { touchEnd.current = e.targetTouches[0].clientX; };
  const handleTouchEnd = () => {
    const distance = touchStart.current - touchEnd.current;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
    touchStart.current = 0;
    touchEnd.current = 0;
  };

  return (
    <div
      className="lux-hero"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {SLIDES.map((slide, index) => {
        let className = "lux-slide";
        if (index === current) className += " active";

        return (
          <div className={className} key={slide.id} style={{ "--theme-color": slide.themeColor }}>
            <div className="lux-bg-text">{slide.category}</div>

            <div className="lux-content">
              <div className="lux-meta">
                <span className="slide-index">0{index + 1}</span>
                <span className="slide-dash"></span>
                <span className="slide-cat">{slide.category}</span>
              </div>

              <h1 className="lux-title" style={{ whiteSpace: "nowrap" }}>
                {slide.title.split(" ").map((word, i) => (
                  <span key={i} className="title-word" style={{ transitionDelay: `${0.1 + i * 0.05}s` }}>
                    {word}&nbsp;
                  </span>
                ))}
              </h1>

              <p className="lux-desc">{slide.description}</p>

              <button
                className="lux-btn"
                onClick={() => handleDiscover(slide.title)}
              >
                <span>Discover Collection</span>
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path fill="currentColor" d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z"/>
                </svg>
              </button>
            </div>

            <div className="lux-image-wrapper">
              <div className="lux-image-mask">
                <img src={slide.image} alt={slide.title} className="lux-img" width={520} height={520} />
              </div>
            </div>
          </div>
        );
      })}

      <div className="lux-controls">
        <button className="control-btn prev" onClick={prevSlide}>←</button>
        <div className="progress-bar-container">
          <div key={current} className="progress-fill" style={{ animationDuration: `${AUTOPLAY_DURATION}ms` }} />
        </div>
        <button className="control-btn next" onClick={nextSlide}>→</button>
      </div>
    </div>
  );
}
